import { signal } from '@preact/signals';
import { useState } from 'preact/hooks';
import type { Language } from '@mxltiverse/shared';
import { sfx } from '../../lib/sfx';
import { translate } from '../../lib/i18n';
import type { Lang } from '../../lib/i18n';
import { currentScreen, gameSetup } from '../../App';

type AuthMode = 'login' | 'register';

function detectBrowserLanguage(): Language {
  const lang = navigator.language.slice(0, 2).toLowerCase();
  if (lang === 'es' || lang === 'en' || lang === 'pt') return lang;
  return 'en';
}

const authMode = signal<AuthMode>('register');
const loading = signal(false);
const feedback = signal<{ type: 'success' | 'error'; message: string } | null>(null);

export function AuthScreen() {
  const lang = (gameSetup.value.language ?? 'es') as Lang;
  const t = (k: Parameters<typeof translate>[0]) => translate(k, lang);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [gender, setGender] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [language, setLanguage] = useState<Language>(detectBrowserLanguage());
  const [code, setCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    sfx.click();
    loading.value = true;
    feedback.value = null;

    try {
      if (!otpSent) {
        const endpoint = authMode.value === 'register' ? '/api/auth/register' : '/api/auth/otp';
        const body =
          authMode.value === 'register'
            ? { full_name: fullName, gender, birth_year: Number(birthYear), email, preferred_language: language }
            : { email };

        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(body),
        });

        const data = await res.json();

        if (!res.ok) {
          sfx.error();
          feedback.value = { type: 'error', message: data.error || 'Error' };
        } else {
          sfx.success();
          setOtpSent(true);
          if (data.dev_otp) {
            feedback.value = { type: 'success', message: `${t('otp_sent_dev')} ${data.dev_otp}` };
          } else {
            feedback.value = { type: 'success', message: t('otp_sent_email') };
          }
        }
      } else {
        const res = await fetch('/api/auth/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email, code }),
        });

        const data = await res.json();

        if (!res.ok) {
          sfx.error();
          feedback.value = { type: 'error', message: data.error || 'Error' };
        } else {
          sfx.verify();
          gameSetup.value = { ...gameSetup.value, language };
          currentScreen.value = 'universe';
        }
      }
    } catch {
      sfx.error();
      feedback.value = { type: 'error', message: 'Network error' };
    } finally {
      loading.value = false;
    }
  }

  return (
    <div class="center-screen">
      <div class="pixel-border container">
        <h2 class="pixel-title text-yellow mb-2" style={{ fontSize: '18px' }}>
          {authMode.value === 'register' ? t('register') : t('login')}
        </h2>

        {!otpSent && (
          <div class="mb-2" style={{ display: 'flex', gap: '1rem' }}>
            <button
              class="pixel-btn"
              style={authMode.value === 'register' ? { borderColor: 'var(--accent-green)' } : {}}
              onClick={() => { sfx.toggle(); authMode.value = 'register'; }}
            >
              {t('register_tab')}
            </button>
            <button
              class="pixel-btn"
              style={authMode.value === 'login' ? { borderColor: 'var(--accent-green)' } : {}}
              onClick={() => { sfx.toggle(); authMode.value = 'login'; }}
            >
              {t('login_tab')}
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {!otpSent && authMode.value === 'register' && (
            <>
              <input
                class="pixel-input"
                type="text"
                placeholder={t('full_name')}
                value={fullName}
                onFocus={() => sfx.focus()}
                onInput={(e) => { sfx.key(); setFullName((e.target as HTMLInputElement).value); }}
                required
              />
              <select
                class="pixel-input"
                value={gender}
                onFocus={() => sfx.focus()}
                onChange={(e) => { sfx.select(); setGender((e.target as HTMLSelectElement).value); }}
              >
                <option value="">{t('gender')}...</option>
                <option value="hombre">{t('male')}</option>
                <option value="mujer">{t('female')}</option>
                <option value="no_especificar">{t('no_specify')}</option>
              </select>
              <input
                class="pixel-input"
                type="number"
                placeholder={t('birth_year')}
                value={birthYear}
                onFocus={() => sfx.focus()}
                onInput={(e) => { sfx.key(); setBirthYear((e.target as HTMLInputElement).value); }}
                required
              />
              <select
                class="pixel-input"
                value={language}
                onFocus={() => sfx.focus()}
                onChange={(e) => { sfx.select(); setLanguage((e.target as HTMLSelectElement).value as Language); }}
              >
                <option value="es">Espanol</option>
                <option value="en">English</option>
                <option value="pt">Portugues</option>
              </select>
            </>
          )}

          {!otpSent && (
            <input
              class="pixel-input"
              type="email"
              placeholder={t('email')}
              value={email}
              onFocus={() => sfx.focus()}
              onInput={(e) => { sfx.key(); setEmail((e.target as HTMLInputElement).value); }}
              required
            />
          )}

          {otpSent && (
            <input
              class="pixel-input"
              type="text"
              placeholder={t('otp_code')}
              value={code}
              maxLength={6}
              onFocus={() => sfx.focus()}
              onInput={(e) => { sfx.key(); setCode((e.target as HTMLInputElement).value); }}
              required
            />
          )}

          {feedback.value && (
            <p class={feedback.value.type === 'error' ? 'text-red' : 'text-green'}>
              {feedback.value.message}
            </p>
          )}

          <button class="pixel-btn text-green" type="submit" disabled={loading.value}>
            {loading.value ? '...' : otpSent ? t('verify') : authMode.value === 'register' ? t('send_code') : t('access')}
          </button>
        </form>

        {otpSent && (
          <button
            class="pixel-btn text-dim mt-2"
            onClick={() => { sfx.click(); setOtpSent(false); }}
            style={{ fontSize: '14px' }}
          >
            {t('back')}
          </button>
        )}
      </div>
    </div>
  );
}
